import { AlertTriangle, Check, Loader2, Save, Search, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useCercaClienti,
  useM3AggiornaImpostazioni,
  useM3EliminaPreferenza,
  useM3Impostazioni,
  useM3Paesi,
  useM3PreferenzeCliente,
  useM3UpsertPaese,
  useM3UpsertPreferenza,
} from "@/hooks/useM3";
import { useMyPermessi } from "@/hooks/usePermessi";
import { cn } from "@/lib/utils";
import type {
  ImpostazioniGlobaliUpdate,
  ModalitaAvviso,
  Paese,
  PaeseUpsert,
  PreferenzaCliente,
  PreferenzaClienteUpsert,
  RegimePaese,
  StrategiaSplit,
} from "@/types/api";
import { AgenteSection } from "./AgenteSection";
import { AssociazioniEmailSection } from "./AssociazioniEmailSection";
import { SetDocumentiSection } from "./SetDocumentiSection";
import { TemplateSection } from "./TemplateSection";
import { RegimeTag } from "./shared";

type ImpostazioniTab = "generali" | "paesi" | "template" | "agente" | "email";

const INPUT =
  "h-9 w-full rounded-md border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export function ImpostazioniView() {
  const { data: perms } = useMyPermessi();
  const isStaff = perms?.is_maitime_staff ?? false;
  // I non-staff col permesso granulare vedono SOLO la gestione associazioni
  // email; le altre sezioni restano riservate allo staff Maitime.
  const tabs: [ImpostazioniTab, string][] = isStaff
    ? [
        ["generali", "Generali"],
        ["paesi", "Paesi & Regimi"],
        ["template", "Template"],
        ["agente", "Agente"],
        ["email", "Associazioni email"],
      ]
    : [["email", "Associazioni email"]];
  const [tab, setTab] = useState<ImpostazioniTab>(tabs[0][0]);
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              tab === k
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "generali" && (
        <div className="space-y-8">
          <ImpostazioniGlobaliForm />
          <PreferenzeClienteSection />
        </div>
      )}
      {tab === "paesi" && (
        <div className="space-y-8">
          <PaesiRegimiSection />
          <SetDocumentiSection />
        </div>
      )}
      {tab === "template" && <TemplateSection />}
      {tab === "agente" && <AgenteSection />}
      {tab === "email" && <AssociazioniEmailSection />}
    </div>
  );
}

function ImpostazioniGlobaliForm() {
  const { data: imp, isLoading } = useM3Impostazioni();
  const aggiorna = useM3AggiornaImpostazioni();
  const [form, setForm] = useState<ImpostazioniGlobaliUpdate>({});
  // I codici si scrivono come testo («IMBEPAL, 10856») e si interpretano qui:
  // riscrivere il campo dal valore interpretato mangerebbe la virgola appena
  // battuta.
  const [codiciTesto, setCodiciTesto] = useState("");

  useEffect(() => {
    if (imp) {
      setForm({
        anticipo_pre_alert_giorni: imp.anticipo_pre_alert_giorni,
        capienza_standard: imp.capienza_standard,
        capienza_max: imp.capienza_max,
        tempo_nota_a_baia_giorni: imp.tempo_nota_a_baia_giorni,
        modalita_avviso_default: imp.modalita_avviso_default,
        strategia_split_default: imp.strategia_split_default,
        orizzonte_proposta_giorni: imp.orizzonte_proposta_giorni,
        soglia_stralcio_pct: imp.soglia_stralcio_pct,
        soglia_sollecito_giorni: imp.soglia_sollecito_giorni,
        soglia_attesa_spedizione_giorni: imp.soglia_attesa_spedizione_giorni,
        tolleranza_ritiro_giorni: imp.tolleranza_ritiro_giorni,
        soglia_mezzo_pallet_pct: imp.soglia_mezzo_pallet_pct,
        articoli_senza_pallet: imp.articoli_senza_pallet,
      });
      setCodiciTesto((imp.articoli_senza_pallet ?? []).join(", "));
    }
  }, [imp]);

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">Impostazioni globali</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-3xl">
        <NumberField
          label="Capienza standard (pallet)"
          value={form.capienza_standard}
          onChange={(v) => setForm((f) => ({ ...f, capienza_standard: v }))}
        />
        <NumberField
          label="Capienza massima (pallet)"
          value={form.capienza_max}
          onChange={(v) => setForm((f) => ({ ...f, capienza_max: v }))}
        />
        <NumberField
          label="Anticipo pre-alert (giorni)"
          value={form.anticipo_pre_alert_giorni}
          onChange={(v) => setForm((f) => ({ ...f, anticipo_pre_alert_giorni: v }))}
        />
        <NumberField
          label="Tempo nota a baia (giorni)"
          value={form.tempo_nota_a_baia_giorni}
          onChange={(v) => setForm((f) => ({ ...f, tempo_nota_a_baia_giorni: v }))}
        />
        <NumberField
          label="Orizzonte proposta (giorni)"
          value={form.orizzonte_proposta_giorni}
          onChange={(v) => setForm((f) => ({ ...f, orizzonte_proposta_giorni: v }))}
        />
        <NumberField
          label="Soglia stralcio (% del camion)"
          value={form.soglia_stralcio_pct}
          onChange={(v) => setForm((f) => ({ ...f, soglia_stralcio_pct: v }))}
        />
        <NumberField
          label="Soglia sollecito (giorni dall'avviso)"
          value={form.soglia_sollecito_giorni}
          onChange={(v) => setForm((f) => ({ ...f, soglia_sollecito_giorni: v }))}
        />
        {/* La colonna Tempo del tabellone (06-10-2026): dalla NDP in poi conta
            l'attesa della spedizione, e oltre questa soglia la cifra è rossa. */}
        <NumberField
          label="Soglia attesa spedizione (giorni dalla NDP)"
          value={form.soglia_attesa_spedizione_giorni}
          onChange={(v) => setForm((f) => ({ ...f, soglia_attesa_spedizione_giorni: v }))}
        />
        {/* La linea guida della proprietà sul ritiro (08-10-2026): oltre, la merce
            pronta e libera è «oltre tolleranza» in Commesse pronte. Gli accordi dei
            singoli clienti stanno in «Preferenze per cliente». */}
        <NumberField
          label="Tolleranza sul ritiro (giorni, linea guida)"
          value={form.tolleranza_ritiro_giorni}
          onChange={(v) => setForm((f) => ({ ...f, tolleranza_ritiro_giorni: v }))}
        />
        {/* La regola dei pallet (01-10-2026): ricavata dalle NDP, qui per poterla
            cambiare senza toccare il codice. */}
        <NumberField
          label="Mezzo pallet fino al (% del pallet)"
          value={form.soglia_mezzo_pallet_pct}
          onChange={(v) => setForm((f) => ({ ...f, soglia_mezzo_pallet_pct: v }))}
        />
        <label className="space-y-1 text-xs text-muted-foreground sm:col-span-2">
          <span>Articoli che non occupano pallet (codici, separati da virgola)</span>
          <input
            className={INPUT}
            aria-label="Articoli che non occupano pallet"
            value={codiciTesto}
            onChange={(e) => {
              setCodiciTesto(e.target.value);
              setForm((f) => ({
                ...f,
                articoli_senza_pallet: e.target.value
                  .split(",")
                  .map((c) => c.trim().toUpperCase())
                  .filter((c, i, a) => c && a.indexOf(c) === i),
              }));
            }}
          />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Modalità avviso default</span>
          <select
            className={INPUT}
            value={form.modalita_avviso_default ?? "manuale"}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                modalita_avviso_default: e.target.value as ModalitaAvviso,
              }))
            }
          >
            <option value="manuale">Manuale</option>
            <option value="automatico">Automatico</option>
          </select>
        </label>
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Strategia split default</span>
          <select
            className={INPUT}
            value={form.strategia_split_default ?? "saturazione"}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                strategia_split_default: e.target.value as StrategiaSplit,
              }))
            }
          >
            <option value="saturazione">Saturazione (riempi i camion)</option>
            <option value="conservativa">Conservativa (lascia margine)</option>
          </select>
        </label>
      </div>
      <button
        type="button"
        onClick={() => aggiorna.mutate(form)}
        disabled={aggiorna.isPending}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {aggiorna.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Save className="size-4" />
        )}
        Salva impostazioni
      </button>
      {aggiorna.isSuccess && <span className="ml-2 text-xs text-emerald-700">Salvato.</span>}
    </section>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <label className="space-y-1 text-xs text-muted-foreground">
      <span>{label}</span>
      <input
        type="number"
        className={INPUT}
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

/**
 * Preferenze per cliente (rifatta l'08-10-2026). Enrico, provando a mettere
 * l'accordo di WONDERFOOD: «deve essere più user friendly, voglio una ricerca
 * del cliente poi la possibilità di selezionarlo e modificare il dato». Prima
 * bisognava conoscere il codice a memoria e batterlo in una casella.
 *
 * Ora: si cerca per nome o codice (la stessa ricerca del «Chi è?» del
 * tabellone), si sceglie, e si modifica in una scheda che mostra i valori
 * attuali. Le righe della tabella si aprono nella stessa scheda.
 */
function PreferenzeClienteSection() {
  const { data: prefs, isLoading } = useM3PreferenzeCliente();
  const { data: imp } = useM3Impostazioni();
  const elimina = useM3EliminaPreferenza();
  const [q, setQ] = useState("");
  const [qCerca, setQCerca] = useState("");
  const [scelto, setScelto] = useState<ClienteScelto | null>(null);
  // L'elenco dei risultati si chiude con Esc o cliccando fuori, e si riapre
  // tornando nella casella: non deve restare sopra la scheda.
  const [aperto, setAperto] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setQCerca(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const trovati = useCercaClienti(qCerca);
  const lineaGuida = imp?.tolleranza_ritiro_giorni;
  const perCodice = useMemo(
    () => new Map((prefs ?? []).map((p) => [p.cliente_codice, p])),
    [prefs]
  );
  // In cima chi ha un accordo sul ritiro: è il dato che si viene a guardare.
  const ordinate = useMemo(
    () =>
      [...(prefs ?? [])].sort(
        (a, b) =>
          Number(b.tolleranza_ritiro_giorni != null) - Number(a.tolleranza_ritiro_giorni != null) ||
          (a.cliente_ragione_sociale ?? "").localeCompare(b.cliente_ragione_sociale ?? "")
      ),
    [prefs]
  );

  function scegli(c: ClienteScelto) {
    setScelto(c);
    setQ("");
    setQCerca("");
    setAperto(false);
  }

  return (
    <section className="space-y-3" data-testid="preferenze-cliente">
      <h2 className="text-sm font-semibold text-foreground">Preferenze per cliente</h2>
      <p className="max-w-3xl text-xs text-muted-foreground">
        Cerca il cliente, sceglilo e modifica i suoi valori. La <b>tolleranza sul ritiro</b> è
        l'accordo del cliente (es. 60 giorni): vuota, vale la linea guida qui sopra
        {lineaGuida != null ? ` (${lineaGuida} giorni)` : ""}. La capienza override cambia la
        proposta di carico per quel cliente.
      </p>

      <div className="relative max-w-xl">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          className={cn(INPUT, "pl-8")}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setAperto(true);
          }}
          onFocus={() => setAperto(true)}
          // Il clic su un risultato arriva dopo il blur: un attimo di margine.
          onBlur={() => setTimeout(() => setAperto(false), 150)}
          onKeyDown={(e) => e.key === "Escape" && setAperto(false)}
          placeholder="Cerca cliente per nome o codice…"
          aria-label="Cerca cliente"
        />
        {aperto && q.trim().length >= 2 && qCerca.length >= 2 && (
          <div
            className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-card shadow-lg"
            role="listbox"
            data-testid="risultati-clienti"
          >
            {trovati.isLoading ? (
              <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" /> Cerco…
              </div>
            ) : (trovati.data ?? []).length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">Nessun cliente trovato.</div>
            ) : (
              (trovati.data ?? []).map((c) => {
                const p = perCodice.get(c.codice);
                return (
                  <button
                    key={c.codice}
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() =>
                      scegli({ codice: c.codice, nome: c.ragione_sociale, paese: c.paese })
                    }
                    className="flex w-full items-center gap-2 border-b px-3 py-2 text-left text-sm last:border-0 hover:bg-muted"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{c.ragione_sociale}</span>
                    <span className="font-mono text-xs text-muted-foreground">#{c.codice}</span>
                    {c.paese && <span className="text-xs text-muted-foreground">{c.paese}</span>}
                    {p?.tolleranza_ritiro_giorni != null && (
                      <span className="rounded-full border px-1.5 text-[10px] text-muted-foreground">
                        accordo {p.tolleranza_ritiro_giorni} gg
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {scelto && (
        <SchedaPreferenza
          key={scelto.codice}
          cliente={scelto}
          pref={perCodice.get(scelto.codice)}
          lineaGuida={lineaGuida}
          onChiudi={() => setScelto(null)}
        />
      )}

      {isLoading ? (
        <Skeleton className="h-24 w-full max-w-3xl" />
      ) : ordinate.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nessuna preferenza configurata.</p>
      ) : (
        <div className="max-w-3xl overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Cliente</th>
                <th className="px-3 py-2 text-right">Tolleranza ritiro</th>
                <th className="px-3 py-2 text-left">Ritiro</th>
                <th className="px-3 py-2 text-right">Capienza</th>
                <th className="px-3 py-2 text-right">Anticipo</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {ordinate.map((p) => (
                <tr
                  key={p.cliente_codice}
                  data-cliente={p.cliente_codice}
                  onClick={() =>
                    scegli({
                      codice: p.cliente_codice,
                      nome: p.cliente_ragione_sociale || `Cliente ${p.cliente_codice}`,
                    })
                  }
                  className={cn(
                    "cursor-pointer border-t hover:bg-muted/40",
                    scelto?.codice === p.cliente_codice && "bg-brand-50"
                  )}
                  title="Apri per modificare"
                >
                  <td className="px-3 py-2">
                    {p.cliente_ragione_sociale || `Cliente ${p.cliente_codice}`}{" "}
                    <span className="font-mono text-xs text-muted-foreground">
                      #{p.cliente_codice}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    {p.tolleranza_ritiro_giorni != null ? (
                      <b>{p.tolleranza_ritiro_giorni} gg</b>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        linea guida{lineaGuida != null ? ` (${lineaGuida})` : ""}
                      </span>
                    )}
                  </td>
                  {/* Marcata dal Portafoglio M1: qui si legge soltanto. */}
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                    {p.modalita_ritiro === "a_chiamata" ? "a chiamata" : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">{p.capienza_override ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{p.anticipo_giorni ?? "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // La riga porta anche la marcatura «a chiamata» del cliente:
                        // eliminarla per togliere un accordo la cancellerebbe.
                        if (
                          !window.confirm(
                            p.modalita_ritiro === "a_chiamata"
                              ? `${p.cliente_ragione_sociale ?? p.cliente_codice} è marcato «a chiamata»: eliminando la preferenza si perde anche questa marcatura. Per togliere solo l'accordo sul ritiro aprilo e usa «Torna alla linea guida». Eliminare comunque?`
                              : `Eliminare tutte le preferenze di ${p.cliente_ragione_sociale ?? p.cliente_codice}?`
                          )
                        )
                          return;
                        if (scelto?.codice === p.cliente_codice) setScelto(null);
                        elimina.mutate(p.cliente_codice);
                      }}
                      className="text-muted-foreground hover:text-red-600"
                      title="Elimina preferenza"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type ClienteScelto = { codice: number; nome: string; paese?: string | null };

const numeroOVuoto = (testo: string): number | null => (testo.trim() === "" ? null : Number(testo));

/** La scheda di un cliente: i suoi valori attuali, da modificare e salvare. */
function SchedaPreferenza({
  cliente,
  pref,
  lineaGuida,
  onChiudi,
}: {
  cliente: ClienteScelto;
  pref: PreferenzaCliente | undefined;
  lineaGuida: number | undefined;
  onChiudi: () => void;
}) {
  const upsert = useM3UpsertPreferenza();
  const [tolleranza, setTolleranza] = useState(
    pref?.tolleranza_ritiro_giorni != null ? String(pref.tolleranza_ritiro_giorni) : ""
  );
  const [capienza, setCapienza] = useState(
    pref?.capienza_override != null ? String(pref.capienza_override) : ""
  );
  const [salvato, setSalvato] = useState(false);

  // Si manda solo quello che è cambiato: l'upsert scrive tutto ciò che riceve,
  // e un campo non toccato mandato vuoto cancellerebbe il valore salvato.
  const body: PreferenzaClienteUpsert = {};
  const t = numeroOVuoto(tolleranza);
  const c = numeroOVuoto(capienza);
  if (t !== (pref?.tolleranza_ritiro_giorni ?? null)) body.tolleranza_ritiro_giorni = t;
  if (c !== (pref?.capienza_override ?? null)) body.capienza_override = c;
  const valido =
    (t == null || (Number.isInteger(t) && t >= 0 && t <= 365)) &&
    (c == null || (Number.isInteger(c) && c > 0));
  const cambiato = Object.keys(body).length > 0;

  function salva(b: PreferenzaClienteUpsert) {
    setSalvato(false);
    upsert.mutate(
      { clienteCodice: cliente.codice, body: { cliente_ragione_sociale: cliente.nome, ...b } },
      { onSuccess: () => setSalvato(true) }
    );
  }

  return (
    <div
      className="max-w-3xl space-y-3 rounded-lg border border-brand-300 bg-brand-50/40 p-4"
      data-testid="scheda-preferenza"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold">{cliente.nome}</div>
          <div className="text-xs text-muted-foreground">
            #{cliente.codice}
            {cliente.paese ? ` · ${cliente.paese}` : ""}
            {pref?.modalita_ritiro === "a_chiamata" && " · marcato «a chiamata» nel Portafoglio M1"}
            {!pref && " · nessuna preferenza finora"}
          </div>
        </div>
        <button
          type="button"
          onClick={onChiudi}
          className="rounded p-1 text-muted-foreground hover:bg-muted"
          aria-label="Chiudi"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span>Tolleranza sul ritiro (giorni)</span>
          <input
            type="number"
            min={0}
            max={365}
            className={INPUT}
            style={{ width: 170 }}
            value={tolleranza}
            onChange={(e) => {
              setTolleranza(e.target.value);
              setSalvato(false);
            }}
            placeholder={lineaGuida != null ? `linea guida: ${lineaGuida}` : "linea guida"}
            aria-label="Tolleranza sul ritiro (giorni)"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span>Capienza override (pallet)</span>
          <input
            type="number"
            min={1}
            className={INPUT}
            style={{ width: 170 }}
            value={capienza}
            onChange={(e) => {
              setCapienza(e.target.value);
              setSalvato(false);
            }}
            placeholder="standard"
            aria-label="Capienza override (pallet)"
          />
        </label>
        <button
          type="button"
          onClick={() => salva(body)}
          disabled={!cambiato || !valido || upsert.isPending}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {upsert.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Salva
        </button>
        {pref?.tolleranza_ritiro_giorni != null && (
          <button
            type="button"
            onClick={() => {
              setTolleranza("");
              salva({ tolleranza_ritiro_giorni: null });
            }}
            disabled={upsert.isPending}
            className="h-9 rounded-md border px-3 text-sm hover:bg-accent disabled:opacity-50"
          >
            Torna alla linea guida
          </button>
        )}
      </div>
      {!valido && (
        <p className="text-xs text-red-700">
          La tolleranza va da 0 a 365 giorni; la capienza è un numero di pallet.
        </p>
      )}
      {salvato && !cambiato && (
        <p
          className="inline-flex items-center gap-1 text-xs text-emerald-700"
          data-testid="salvato"
        >
          <Check className="size-3.5" /> Salvato.
          {t != null
            ? ` Accordo: ${t} giorni per ritirare la merce pronta.`
            : ` Vale la linea guida${lineaGuida != null ? ` (${lineaGuida} giorni)` : ""}.`}
        </p>
      )}
    </div>
  );
}

const REGIMI: { value: RegimePaese; label: string }[] = [
  { value: "italia", label: "Nazionale" },
  { value: "ue", label: "Intra CEE" },
  { value: "extra_ue", label: "Extra CEE" },
  { value: "bielorussia", label: "Bielorussia" },
];

function PaesiRegimiSection() {
  const { data: paesi, isLoading } = useM3Paesi();
  const upsert = useM3UpsertPaese();
  const [q, setQ] = useState("");
  const [soloDaVerif, setSoloDaVerif] = useState(false);

  function salva(p: Paese, patch: Partial<PaeseUpsert>) {
    upsert.mutate({
      sigla: p.sigla,
      body: {
        nome: p.nome,
        regime: p.regime,
        da_verificare: p.da_verificare,
        ...patch,
      },
    });
  }

  const visibili = useMemo(() => {
    if (!paesi) return [];
    const t = q.toLowerCase().trim();
    return paesi.filter(
      (p) =>
        (!soloDaVerif || p.da_verificare) &&
        (!t || p.sigla.toLowerCase().includes(t) || (p.nome ?? "").toLowerCase().includes(t))
    );
  }, [paesi, q, soloDaVerif]);

  const nDaVerif = paesi?.filter((p) => p.da_verificare).length ?? 0;

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Paesi & Regimi</h2>
        <p className="text-xs text-muted-foreground">
          Sigla gestionale (colonna «Stato» dell'anagrafica) → regime doganale. Guida il tag della
          proposta e i documenti da generare. I codici «da verificare» vanno controllati a mano.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca sigla o paese…"
            className={cn(INPUT, "w-56 pl-8")}
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={soloDaVerif}
            onChange={(e) => setSoloDaVerif(e.target.checked)}
          />
          Solo da verificare ({nDaVerif})
        </label>
        {upsert.isPending && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full max-w-3xl" />
      ) : (
        <div className="max-w-3xl overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Sigla</th>
                <th className="px-3 py-2 text-left">Paese</th>
                <th className="px-3 py-2 text-left">Regime</th>
                <th className="px-3 py-2 text-right">Clienti</th>
                <th className="px-3 py-2 text-center">Stato</th>
              </tr>
            </thead>
            <tbody>
              {visibili.map((p) => (
                <tr key={p.sigla} className={cn("border-t", p.da_verificare && "bg-amber-50/40")}>
                  <td className="px-3 py-1.5 font-mono text-xs">{p.sigla}</td>
                  <td className="px-3 py-1.5">
                    <input
                      key={`${p.sigla}-${p.nome}`}
                      defaultValue={p.nome ?? ""}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (p.nome ?? "")) salva(p, { nome: v || null });
                      }}
                      className="h-7 w-full rounded border bg-card px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <select
                        value={p.regime}
                        onChange={(e) => salva(p, { regime: e.target.value as RegimePaese })}
                        className="h-7 rounded border bg-card px-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      >
                        {REGIMI.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                      <RegimeTag regime={p.regime} />
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                    {p.n_clienti}
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() => salva(p, { da_verificare: !p.da_verificare })}
                      title={p.da_verificare ? "Segna come verificato" : "Segna come da verificare"}
                      className={cn(
                        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]",
                        p.da_verificare
                          ? "bg-amber-100 text-amber-800"
                          : "text-muted-foreground hover:bg-muted"
                      )}
                    >
                      {p.da_verificare ? (
                        <>
                          <AlertTriangle className="size-3" />
                          da verificare
                        </>
                      ) : (
                        "ok"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
              {visibili.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-xs text-muted-foreground">
                    Nessun paese per «{q}».
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
